import { WEAPONS, PASSIVES, isEvolved } from "../sim/upgrades.js";
import { weaponName, passiveName } from "./labels.js";

// 強化の状態のレーダーチャート(2026-11-03 オーナー要望。カードの説明から数値を外した代わりに、育ち具合を形で見せる)。
// - 軸は武器5つ + 強化5つの10本で、いつも同じ十角形(取っていないものは0で、名前を薄く出す)。
// - 進化の相方どうし(ビーム砲と出力強化など。WEAPONS.<武器>.evolution.with)を隣に並べる。
// - 外周の枠が Lv5。進化した武器だけ、枠の外へ1段(Lv6 相当)金色で伸ばす。
// レベルアップの画面と、プレイ中の右上のボタンから開く(src/main.js の openChart)。
// レベルアップの画面から開いたときは、出ているカードの軸の名前をボタンにし(押すとその場で選ぶ)、
// 選ぶと伸びる分を別の色(オレンジ)で先に見せる(2026-11-03 オーナー要望「選んだ時に伸びる線も色違いで事前に示しておく」)。

const W = 440; // viewBox の幅(横の軸の名前が長いので横長)
const H = 310;
const CX = W / 2;
const CY = H / 2;
const R = 88; // Lv5 の半径(進化の Lv6 は 1.2 倍)
const MAX = 5; // 枠の外周のレベル

// 軸の並び: 武器 → その相方の強化 → 次の武器 …
export function radarAxes() {
  const axes = [];
  for (const [id, w] of Object.entries(WEAPONS)) {
    axes.push({ kind: "weapon", id });
    if (w.evolution && PASSIVES[w.evolution.with]) axes.push({ kind: "passive", id: w.evolution.with });
  }
  // 相方のいない強化があれば最後に足す
  for (const id of Object.keys(PASSIVES)) if (!axes.some((a) => a.kind === "passive" && a.id === id)) axes.push({ kind: "passive", id });
  return axes;
}

// player({ weapons, passives })の各軸のレベル・名前・進化したか
export function radarValues(player) {
  return radarAxes().map((a) => {
    const lv = (a.kind === "weapon" ? player.weapons[a.id] : player.passives[a.id]) || 0;
    const evolved = a.kind === "weapon" && lv > 0 && isEvolved(a.id, lv);
    return { ...a, lv, evolved, name: a.kind === "weapon" ? weaponName(a.id, lv) : passiveName(a.id) };
  });
}

const f = (v) => v.toFixed(1);
const at = (i, n, r) => {
  const a = -Math.PI / 2 + (i / n) * Math.PI * 2; // 真上から時計回り
  return [CX + Math.cos(a) * r, CY + Math.sin(a) * r];
};
const poly = (pts) => pts.map(([x, y]) => `${f(x)},${f(y)}`).join(" ");
const OFFER = "#ff9a3a"; // 選ぶと伸びる分の色(今の青・進化の金と見分ける)
// 名前の幅のおおよそ(SVG の文字は測れないので、全角 12.5・半角 7.2 で数える)
const textW = (s, size = 12.5) => [...String(s)].reduce((w, c) => w + (c.charCodeAt(0) > 0xff ? size : size * 0.58), 0);
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");

// offers: レベルアップの選択肢(src/sim/upgrades.js の choices)。武器・強化・進化だけ軸に出る(SHIELD回復は軸が無い)
export function radarSvg(player, offers = []) {
  const vals = radarValues(player);
  const offerAt = (v) => offers.findIndex((c) => (c.kind === "passive" ? "passive" : "weapon") === v.kind && c.id === v.id && c.kind !== "heal");
  const n = vals.length;
  const out = [`<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img">`];
  // 目盛り(Lv1〜5 の十角形)と軸
  for (let lv = 1; lv <= MAX; lv++) {
    const pts = vals.map((_, i) => at(i, n, (R * lv) / MAX));
    out.push(`<polygon points="${poly(pts)}" fill="none" stroke="rgba(120,170,255,${lv === MAX ? 0.55 : 0.18})" stroke-width="${lv === MAX ? 1.4 : 1}"/>`);
  }
  vals.forEach((_, i) => {
    const [x, y] = at(i, n, R);
    out.push(`<line x1="${CX}" y1="${CY}" x2="${f(x)}" y2="${f(y)}" stroke="rgba(120,170,255,0.18)"/>`);
  });
  // 進化の分(金): 実際のレベル(Lv6 まで)の形を先に塗り、上から Lv5 までの形を重ねる → 枠の外へ出た分だけ金色に見える
  if (vals.some((v) => v.evolved)) {
    const pts = vals.map((v, i) => at(i, n, (R * v.lv) / MAX));
    out.push(`<polygon points="${poly(pts)}" fill="rgba(255,210,58,0.55)" stroke="#ffd23a" stroke-width="2" stroke-linejoin="round"/>`);
  }
  const pts = vals.map((v, i) => at(i, n, (R * Math.min(v.lv, MAX)) / MAX));
  out.push(`<polygon points="${poly(pts)}" fill="rgba(73,230,255,0.32)" stroke="#49e6ff" stroke-width="2" stroke-linejoin="round"/>`);
  // 選ぶと伸びる分(オレンジの太線。今のレベルの点から、選んだ後のレベルの点まで)
  vals.forEach((v, i) => {
    const k = offerAt(v);
    if (k < 0) return;
    const [x1, y1] = at(i, n, (R * v.lv) / MAX);
    const [x2, y2] = at(i, n, (R * offers[k].level) / MAX);
    out.push(
      `<line x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}" stroke="${OFFER}" stroke-width="5" stroke-linecap="round" opacity="0.9"/>` +
        `<circle cx="${f(x2)}" cy="${f(y2)}" r="4.5" fill="${OFFER}" stroke="#fff" stroke-width="1.2"/>`,
    );
  });
  vals.forEach((v, i) => {
    if (!v.lv) return;
    const [x, y] = v.evolved ? at(i, n, (R * v.lv) / MAX) : pts[i]; // 進化は伸びた先に点
    out.push(`<circle cx="${f(x)}" cy="${f(y)}" r="3" fill="${v.evolved ? "#ffd23a" : "#fff"}"/>`);
  });
  // 名前とレベル(武器は青、強化は緑がかった色。進化は金)
  vals.forEach((v, i) => {
    const [x, y0] = at(i, n, R * 1.2 + 14); // 進化して伸びた先より外
    const dx = x - CX;
    const y = y0 + (y0 < CY - R * 0.8 ? -8 : y0 > CY + R * 0.8 ? 6 : 0); // 真上・真下の名前は軸の先から少し離す
    const anchor = Math.abs(dx) < 8 ? "middle" : dx > 0 ? "start" : "end";
    const color = !v.lv ? "rgba(160,180,210,0.4)" : v.evolved ? "#ffd23a" : v.kind === "weapon" ? "#8fd0ff" : "#9ff0c8";
    const k = offerAt(v);
    if (k >= 0) {
      // 出ているカードの軸: 名前をボタンにする(data-choice = カードの番号。押したら src/main.js が選ぶ)
      const c = offers[k];
      const name = c.kind === "evolve" ? weaponName(c.id, c.level) : v.name;
      const sub = c.kind === "evolve" ? "EVO!" : v.lv ? `Lv${v.lv} → ${c.level}` : "NEW";
      const w = Math.max(textW(name), textW(sub, 11)) + 16;
      const bx0 = anchor === "middle" ? x - w / 2 : anchor === "start" ? x - 8 : x - w + 8;
      const bx = Math.min(W - w - 2, Math.max(2, bx0)); // 長い名前でも枠からはみ出さない(文字も同じだけずらす)
      const tx = x + (bx - bx0);
      out.push(
        `<g class="radar-pick" data-choice="${k}" role="button" tabindex="0">` +
          `<rect x="${f(bx)}" y="${f(y - 19)}" width="${f(w)}" height="38" rx="8" fill="rgba(255,154,58,0.18)" stroke="${OFFER}" stroke-width="1.6"/>` +
          `<text x="${f(tx)}" y="${f(y - 2)}" text-anchor="${anchor}" fill="#fff" font-size="12.5" font-weight="700">${esc(name)}</text>` +
          `<text x="${f(tx)}" y="${f(y + 13)}" text-anchor="${anchor}" fill="${OFFER}" font-size="11" font-weight="700" font-family="Oxanium, sans-serif">${sub}</text>` +
          `</g>`,
      );
      return;
    }
    const lv = !v.lv ? "−" : v.evolved ? "EVO" : `Lv${v.lv}`;
    out.push(
      `<text x="${f(x)}" y="${f(y - 2)}" text-anchor="${anchor}" fill="${color}" font-size="12.5" font-weight="700">${esc(v.name)}</text>` +
        `<text x="${f(x)}" y="${f(y + 13)}" text-anchor="${anchor}" fill="${color}" font-size="11" font-family="Oxanium, sans-serif">${lv}</text>`,
    );
  });
  out.push("</svg>");
  return out.join("");
}

// ボタンに使う小さい絵(十角形の枠・軸・中の形)
export const RADAR_ICON = (() => {
  const n = 10;
  const pt = (i, k) => {
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    return [12 + Math.cos(a) * 9 * k, 12 + Math.sin(a) * 9 * k];
  };
  const pts = (ks) => ks.map((k, i) => pt(i, k).map(f).join(",")).join(" ");
  const spokes = Array.from({ length: n / 2 }, (_, i) => {
    const [[x1, y1], [x2, y2]] = [pt(i, 1), pt(i + n / 2, 1)];
    return `<line x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}" stroke="currentColor" stroke-width="0.8" opacity="0.35"/>`;
  }).join("");
  const shape = [0.9, 0.6, 0.75, 0.85, 0.5, 0.7, 0.95, 0.55, 0.65, 0.8];
  return (
    `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
    `<polygon points="${pts(Array(n).fill(1))}" fill="none" stroke="currentColor" stroke-width="1.3" opacity="0.6"/>` +
    spokes +
    `<polygon points="${pts(shape)}" fill="rgba(73,230,255,0.45)" stroke="#49e6ff" stroke-width="1.4" stroke-linejoin="round"/>` +
    `</svg>`
  );
})();
